import { doc, getDoc } from 'firebase/firestore';
import React, { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { db } from '../../../data/firebase/config';
import { useAuth } from '../../context/auth';
import { DOCUMENTS_DB } from '../../env/documents-db';

const ADMIN_SESSION_CACHE_KEY = 'inter:isAdmin';

interface AdminRouteProps {
  children: ReactNode;
}

/**
 * Componente que protege rutas administrativas
 * Verifica si el usuario tiene acceso al panel de administración
 */
export const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!user) {
        sessionStorage.removeItem(ADMIN_SESSION_CACHE_KEY);
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(
          doc(db, DOCUMENTS_DB.USERS_ADMIN, user.id),
        );

        if (userDoc.exists()) {
          const userData = userDoc.data();

          // Verificar si el usuario está habilitado
          if (userData.isEnabled === false) {
            sessionStorage.removeItem(ADMIN_SESSION_CACHE_KEY);
            setIsAdmin(false);
            setLoading(false);
            return;
          }

          // Verificar si tiene roles RBAC asignados o soporte legacy
          // (role='administrator' sin roles RBAC)
          const confirmedAdmin =
            (userData.roles && userData.roles.length > 0) ||
            userData.role === 'administrator';

          if (confirmedAdmin) {
            sessionStorage.setItem(ADMIN_SESSION_CACHE_KEY, 'true');
          } else {
            sessionStorage.removeItem(ADMIN_SESSION_CACHE_KEY);
          }
          setIsAdmin(confirmedAdmin);
        } else {
          sessionStorage.removeItem(ADMIN_SESSION_CACHE_KEY);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Error checking admin access:', error);
        // Falla transitoria (p. ej. red móvil intermitente): no expulsar a un
        // usuario ya confirmado como admin en esta sesión — usar el valor
        // cacheado en lugar de asumir isAdmin=false.
        setIsAdmin(sessionStorage.getItem(ADMIN_SESSION_CACHE_KEY) === 'true');
      } finally {
        setLoading(false);
      }
    };

    checkAdminAccess();
  }, [user]);

  // Mostrar loading mientras verifica permisos
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Redirigir si no es admin
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Renderizar el contenido si es admin
  return <>{children}</>;
};
